// src/context/AuthContext.js
import { createContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "../utils/axios";

export const AuthContext = createContext();

const TOKEN_KEY = "token";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const mountedRef = useRef(true);

  // ✅ Create BroadcastChannel per tab/provider instance (not global)
  const bcRef = useRef(null);

  const getToken = () => localStorage.getItem(TOKEN_KEY);

  const safePost = (payload) => {
    try {
      if (bcRef.current) bcRef.current.postMessage(payload);
    } catch (e) {
      // ignore "Channel is closed" or unsupported errors
    }
  };

  const clearAuth = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const fetchProfile = async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get("/users/me");
      if (!mountedRef.current) return;
      setUser(res.data);
    } catch (err) {
      if (!mountedRef.current) return;
      clearAuth();
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // Initial load + setup BC
  useEffect(() => {
    mountedRef.current = true;

    // Setup BroadcastChannel if supported
    if ("BroadcastChannel" in window) {
      bcRef.current = new BroadcastChannel("arosia-auth");
      bcRef.current.onmessage = (event) => {
        const { type } = event.data || {};
        if (type === "LOGOUT") {
          setUser(null);
          setLoading(false);
        }
        if (type === "LOGIN") {
          fetchProfile();
        }
      };
    }

    fetchProfile();

    return () => {
      mountedRef.current = false;
      // ✅ Do NOT close bc here (prevents "Channel is closed" crashes)
      // If you really want, you can close safely:
      // try { bcRef.current?.close?.(); } catch {}
      // bcRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Cross-tab sync using storage event (works always)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== TOKEN_KEY) return;

      // token removed => logout everywhere
      if (!e.newValue) {
        setUser(null);
        setLoading(false);
        return;
      }

      // token added/changed => refetch profile
      fetchProfile();
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    setLoading(true);
    try {
      const res = await axios.post("/users/login", { username, password });

      localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser(res.data.user);

      // notify other tabs
      safePost({ type: "LOGIN" });

      return res.data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAuth();

    // notify other tabs (safe)
    safePost({ type: "LOGOUT" });

    // also triggers other tabs via storage event automatically
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      refreshProfile: fetchProfile,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};